import { DevTool } from "@hookform/devtools";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form } from "antd";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import React, { PropsWithChildren } from "react";
import {
    DefaultValues,
    FieldErrors,
    FieldValues,
    FormProvider,
    ResolverOptions,
    ResolverResult,
    SubmitErrorHandler,
    SubmitHandler,
    useForm,
} from "react-hook-form";
import { Nullable } from "types/models";
import { ZodType } from "zod";

const shouldShowFormDevTool = () => {
    if (!import.meta.env.DEV || typeof window === "undefined") {
        return false;
    }

    try {
        return window.localStorage.getItem("paldock:form-devtools") === "1";
    } catch {
        return false;
    }
};

export interface GeneralFormProviderProps<T extends FieldValues> extends PropsWithChildren {
    schema: ZodType<T>;
    onSubmit?: SubmitHandler<T>;
    /** Called with the errors and the current values when the schema rejects a submit */
    onInvalid?: (errors: FieldErrors<T>, values: T) => void;
    defaultValues?: DefaultValues<Nullable<T>>;
    id?: string;
    resetOnSubmit?: boolean;
    requiredMark?: boolean;
}

const customizeRequiredMark = (label: React.ReactNode, { required }: { required: boolean }) => (
    <>
        {label} {required ? <span className={"ml-1 text-[#b91c1c]"}>*</span> : null}
    </>
);

export function GeneralFormProvider<T extends FieldValues>({
    schema,
    onSubmit = () => null,
    onInvalid,
    defaultValues,
    children,
    id,
    resetOnSubmit = true,
    requiredMark,
}: GeneralFormProviderProps<T>) {
    const [form] = Form.useForm();
    const { t } = usePaldockTranslation();

    // Create a custom resolver that translates error messages
    const translatedResolver = async (
        values: T,
        context: object | undefined,
        options: ResolverOptions<T>
    ): Promise<ResolverResult<T>> => {
        const result = await zodResolver(schema)(values, context, options);

        // Recursive function to translate error messages at any depth
        const translateErrors = (errors: FieldErrors<T> | Record<string, unknown>): void => {
            Object.keys(errors).forEach((key) => {
                const error = errors[key];
                if (!error) return;

                // Handle array of errors (for field arrays)
                if (Array.isArray(error)) {
                    error.forEach((arrayError) => {
                        if (arrayError && typeof arrayError === "object") {
                            translateErrors(arrayError as Record<string, unknown>);
                        }
                    });
                } else if (typeof error === "object" && error !== null) {
                    // Handle nested objects
                    if ("message" in error && typeof error.message === "string") {
                        // Check if it's a translation key using a more robust pattern
                        // Translation keys follow the pattern: namespace.subnamespace.key (at least 2 dots)
                        // Allows lowercase letters, hyphens, underscores, and numbers in segments
                        const translationKeyPattern = /^[a-z0-9]+([_-][a-z0-9]+)*(\.[a-z0-9]+([_-][a-z0-9]+)*){2,}$/;
                        if (translationKeyPattern.test(error.message)) {
                            const translated = t(error.message);
                            /* v8 ignore next 3 -- translation replacement branch */
                            if (translated !== error.message) {
                                (error as { message: string }).message = translated;
                            }
                        }
                    } else {
                        /* v8 ignore next 2 -- recursive error handling */
                        // Recursively handle nested error objects
                        translateErrors(error as Record<string, unknown>);
                    }
                }
            });
        };

        // Translate error messages if they are translation keys
        if (result.errors) {
            translateErrors(result.errors as FieldErrors<T>);
        }

        return result;
    };

    const methods = useForm<T>({
        defaultValues: defaultValues as DefaultValues<T> | undefined,
        resolver: translatedResolver,
        mode: "onChange",
    });

    const { reset, control, handleSubmit } = methods;
    const showFormDevTool = shouldShowFormDevTool();

    // Debug form state in development
    if (import.meta.env.DEV) {
        console.log("Form state errors:", methods.formState.errors);
        console.log("Form is valid:", methods.formState.isValid);
        console.log("Form values:", methods.getValues());
    }

    return (
        <FormProvider {...methods}>
            <Form
                id={id}
                /* v8 ignore next 6 -- form submit handler requires integration test */
                onFinish={handleSubmit(
                    async (data) => {
                        await onSubmit?.(data);

                        if (resetOnSubmit) {
                            reset();
                        }
                    },
                    ((errors) => onInvalid?.(errors, methods.getValues())) as SubmitErrorHandler<T>
                )}
                form={form}
                layout="vertical"
                variant="outlined"
                requiredMark={requiredMark ?? customizeRequiredMark}
                size="middle"
            >
                {children}
            </Form>
            {showFormDevTool && <DevTool control={control} />}
        </FormProvider>
    );
}
